import { formatDate } from '@angular/common';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';
import { isBoolean, isNullOrUndefined, isString } from '../../../models/utils';
import { CellActionType, CellTemplateType, GenericColDef, GridContext } from '../../../models/grid-context';
import * as _ from 'lodash';
import { SecureObjectGroupType } from '../../../models/role';
import { ExceptionDetails } from '../../../models/report-details';
import { AnalyticNodeMode, JobPriorityMode, ReviewSystem } from '../../../models/enums';
import { Job } from '../../../models/job';
import { StorageUseType } from '../../modals/storage-modal/storage-modal.component';
import { SMF_NO_TEXT_MESSAGE } from '../../../models/constants';
import { Workflow } from '../../../models/workflow';

export class GridCellRenderer implements ICellRendererComp {
    params: ICellRendererParams;
    colDef: GenericColDef;
    gridContext: GridContext;
    cellTemplateType: CellTemplateType;
    automationId: string;
    iconClass: string;
    iconSecondClass: string;
    iconThirdClass: string;
    iconColor: string;
    iconToolTip: string;
    iconSecondColor: string;
    iconThirdColor: string;

    cellElement: HTMLElement;
    contextMenuElement: HTMLElement;

    init?(params: ICellRendererParams): void {
        this.params = params;
        this.cellElement = document.createElement('div');
        this.cellElement.className = this.params?.data?.isChild && this.params?.data?.isWorkflowSettingsStep ? 'grid-cell-full' : 'grid-cell';

        if (this.params && this.params.data) {
            this.colDef = this.params.column.getColDef() as GenericColDef;

            this.gridContext = this.params.context as GridContext;
            if (this.colDef) {
                this.cellTemplateType = this.colDef.cellTemplateType || CellTemplateType.basicLabel;
                if (this.cellTemplateType === CellTemplateType.nameIconLabel || this.cellTemplateType === CellTemplateType.linkIconLabel || this.cellTemplateType === CellTemplateType.groupIconLabel ||
                    this.cellTemplateType === CellTemplateType.nameIconLabelTooltip || this.cellTemplateType === CellTemplateType.nameMultipleIconsLabel ||
                    this.cellTemplateType === CellTemplateType.iconOnlyFieldWithTooltip) {
                    this.iconClass = (this.colDef.alternativeFieldNames ? this.params.data[this.colDef.alternativeFieldNames.iconClass] : this.params.data.iconClass) || '';
                    this.iconColor = (this.colDef.alternativeFieldNames ? this.params.data[this.colDef.alternativeFieldNames.iconColor] : this.params.data.iconColor) || '';
                    this.iconToolTip = (this.colDef.alternativeFieldNames ? this.params.data[this.colDef.alternativeFieldNames.tooltip] : this.params.data.tooltip) || '';
                    this.iconSecondClass = this.params.data.iconSecondClass || '';
                    this.iconSecondColor = this.params.data.iconSecondColor || '';
                    this.iconThirdClass = this.params.data.iconThirdClass || '';
                    this.iconThirdColor = this.params.data.iconThirdColor || '';
                }

                if (!this.colDef.automationIdParsed) {
                    this.colDef.automationIdParsed = true;
                    this.colDef.automationId = `${this.colDef.automationId ? (this.colDef.automationId + '-') : ''}grid-cell`;
                    const domainAliasOrEmptyAutoIdPrefix = this.colDef?.colId === 'domainListCustomAutomationId' ? 'domain-name' : this.colDef?.colId === 'aliasListCustomAutomationId' ? 'alias-name' : '';
                    let name = this.colDef.cellTemplateType === CellTemplateType.contextMenu ? 'ellipsis-button' :
                        this.colDef.cellTemplateType === CellTemplateType.cellButton ? 'button' : domainAliasOrEmptyAutoIdPrefix;
                    if (!name && this.colDef.headerName) {
                        name = this.colDef.headerName.replace(/%/g, 'percent').replace(/[^a-zA-Z0-9-_ ]/gi, '');
                    }
                    if (!name && this.colDef.colId) {
                        name = this.colDef.colId.replace(/[^a-zA-Z0-9-_ ]/gi, '');
                    }
                    if (!name && this.colDef.field) {
                        name = this.colDef.field.replace(/[^a-zA-Z0-9-_ ]/gi, '');
                    }
                    if (name) {
                        this.colDef.automationId += `-${name.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase()}`;
                    }
                }
                this.automationId = this.colDef.automationId;

                this.cellElement.oncontextmenu = this.onCellContextMenu;
                this.cellElement.setAttribute('data-automation-id', this.automationId);

                if (isString(this.params.data[this.colDef.field + 'Tooltip'])) {
                    this.cellElement.title = this.params.data[this.colDef.field + 'Tooltip'];
                }

                if (this.params.data?.deletePending) {
                    this.cellElement.title = this.params.data?.tooltip ? this.params.data?.tooltip : '';
                }
                this.createElement();
            }
        }
    }

    setTitleIfTruncated(this: HTMLElement, event: MouseEvent): void {
        if (this.offsetWidth < this.scrollWidth) {
            this.setAttribute('title', this.innerHTML);
        } else {
            this.removeAttribute('title');
        }
    }

    createElement(): void {
        const htmlParts = [];
        if (this.params?.data?.filetype === 'smf' && this.params?.value === SMF_NO_TEXT_MESSAGE) {
            htmlParts.push(`<span data-automation-id="${this.automationId + '-label'}" style="color: #999999;"`);
        }
        else {
            htmlParts.push(`<span data-automation-id="${this.automationId + '-label'}"`);
        }
        let text = isNullOrUndefined(this.params.value) ? '' : this.params.value.toString();
        text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        let className = '';
        if (this.colDef.cellAlignDirection) {
            className += `align-${this.colDef.cellAlignDirection} `;
        }
        if (this.colDef.preserveSpaces) {
            htmlParts.push(`<pre>`);
            htmlParts.push(text);
            htmlParts.push(`</pre>`);
        }
        let reviewIconColor = null, reviewIconTooltip = null;
        if (this.colDef.reviewIcon) {
            reviewIconColor = this.params?.data?.reviewSystem === ReviewSystem.REEF_REVIEW ? 'color-blue-01' :
                this.params?.data?.reviewSystem === ReviewSystem.RELATIVITY ? 'color-tan-07' : null;
            reviewIconTooltip = this.params?.data?.reviewSystem === ReviewSystem.REEF_REVIEW ? 'Reef ECA and Reef Review project' :
                this.params?.data?.reviewSystem === ReviewSystem.RELATIVITY ? 'Reef ECA and Relativity project' : null
        }
        let claimsIconColor = null, claimsIconTooltip = null;
        if (this.colDef.claimsIcon) {
            claimsIconColor = this.params?.data?.attributes?.filter((attr: any) => attr.name === 'CLASS_ACTION')[0]?.value === 'true' ? 'color-blue-01' : null;
            claimsIconTooltip = this.params?.data?.attributes?.filter((attr: any) => attr.name === 'CLASS_ACTION')[0]?.value === 'true' ? 'Reef Claims Project' : null;
        }
        switch (this.cellTemplateType) {
            case CellTemplateType.basicLabel: {
                if (!this.colDef.preserveSpaces) {
                    htmlParts.push(text);
                    break;
                }
                break;
            }
            case CellTemplateType.basicLabelWithTooltip: {
                if (reviewIconColor) {
                    htmlParts.push(`<span title="${reviewIconTooltip}" class="review-icon-group icon-first-margin ${reviewIconColor}"><i class="fa fa-clone"></i><span>R</span></span>`);
                }
                if (this.params.data && this.params.data.attributes && this.params.data.projectActivationState === 'IMPORT_FAILED') {
                    this.params.data.attributes.forEach(element => {
                        if (element.name === 'failureMessage') {
                            htmlParts[0] += ` title="${element.value}" style="pointer-events: initial;"`;
                        }
                    });
                } else {
                    this.cellElement.addEventListener('mouseenter', this.setTitleIfTruncated, false);
                    if (this.params?.value instanceof String || typeof this.params?.value === 'string' || typeof this.params?.value === 'number') {
                        htmlParts[0] += `title="${typeof this.params.value === 'string'
                            ? this.params.value?.replace(/"/g, '&quot;')
                            : this.params.value ? this.params.value : ''}"`;
                    }

                }
                if (!this.colDef.preserveSpaces) {
                    htmlParts.push(text);
                }
                break;
            }
            case CellTemplateType.reviewProjectsWithTooltip: {
                if (this.params?.data?.reviewProjects?.length > 0) {
                    const reviewProjects = _.sortBy(this.params.data.reviewProjects, 'reviewProjectName');
                    let tooltip = '';
                    reviewProjects.forEach(rp => {
                        htmlParts.push(`<span class="review-icon-group ${rp.reviewSystem === ReviewSystem.REEF_REVIEW ? 'color-blue-01' : 'color-tan-07'}"><i class="fa fa-clone"></i><span>R</span></span>`);
                        htmlParts.push(rp.reviewProjectName + '  ');
                        tooltip += (tooltip !== '' ? '; ' : '') + rp.reviewProjectName + (rp.reviewSystem === ReviewSystem.REEF_REVIEW && rp.templateName && this.colDef.addReviewTemplate ? (' (Review Template: ' + rp.templateName + ')') : '');

                    })
                    htmlParts[0] += ` title="${tooltip}" `;
                }
                break;
            }
            case CellTemplateType.date: {
                let varText = text;
                if (text.length > 0 && !isNaN(Number(text))) {
                    varText = formatDate(varText, 'yyyy-MM-dd HH:mm:ss', 'en-US');
                }
                htmlParts[0] += ` title="${varText}" `;
                htmlParts.push(varText);
                break;
            }
            case CellTemplateType.formattedDateOrToday: {
                const dayInMiliseconds = 8.64e7;
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const originalDaysSinceEpoch = Math.floor(Date.parse(text.split(' ')[0]) / dayInMiliseconds),
                    currentMonth = now.getMonth() + 1,
                    currentDate = now.getDate(),
                    todaysDaysSinceEpoch = Math.floor(Date.parse(now.getFullYear() + '-' + (currentMonth < 10 ? '0' + currentMonth : currentMonth) + '-' + (currentDate < 10 ? '0' + currentDate : currentDate)) / dayInMiliseconds);
                if (originalDaysSinceEpoch === todaysDaysSinceEpoch) {
                    text = 'Today ' + text.split(' ')[1];
                } else if (originalDaysSinceEpoch + 1 === todaysDaysSinceEpoch) {
                    text = 'Yesterday ' + text.split(' ')[1];
                }
                text = text.substring(0, text.lastIndexOf('.'));
                htmlParts[0] += ` title="${text}" `;
                htmlParts.push(text);
                break;
            }
            case CellTemplateType.nameIconLabel: {
                if (this.params.data.level) {
                    htmlParts[0] += ` style="margin-left: ${(10 + this.params.data.level * 15) + 'px'}"`;
                }
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${(this.iconThirdClass) ? 'fa-stack-1x' : ''} ${this.iconClass} ${this.iconColor}" style="color: ${this.iconColor}; margin-right: ${text ? '10px' : '0px'}"></i>`);
                    if (this.params?.data?.iconSecondClass) {
                        const iconSecondClass = this.params?.data?.iconSecondClass;
                        const iconSecondColor = this.params?.data?.iconSecondColor;
                        const secondClass = this.params?.data?.isThread ? 'gtn-second-icon-threads' : 'gtn-second-icon';
                        htmlParts.push(`<i class="fa ${secondClass} ${iconSecondClass} ${iconSecondColor ? iconSecondColor : this.iconColor}"></i>`);
                    }
                    if (this.params?.data?.iconThirdClass) {
                        const iconThirdClass = this.params?.data?.iconThirdClass;
                        const iconThirdColor = this.params?.data?.iconThirdColor;
                        htmlParts.push(`<i class="fa fa-stack-1x gtn-second-icon ${iconThirdClass} ${iconThirdColor ? iconThirdColor : this.iconColor}"></i>`);
                    }
                }
                if (this.params.data.isRoleHeader) {
                    className += ' role-header-cell';
                }
                htmlParts.push(text);
                if (this.params.data?.showDrillHelper) {
                    htmlParts.push(`. <span class="drill-helper-text">Double-click row to view documents.</span>`);
                }
                break;
            }
            case CellTemplateType.nameMultipleIconsLabel: {
                if (this.params?.data?.isTemplateProfileRow) {
                    className += 'template-profile-type';
                }
                const useTemplateCustomClass = this.params?.data?.isTemplate || this.params?.data?.isTemplateProfileRow;
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor}"></i>`);
                    if (this.iconSecondClass) {
                        const secondClass = useTemplateCustomClass ? 'gtn-second-icon-template' : 'gtn-second-icon';
                        htmlParts.push(`<i class="fa ${secondClass} ${this.iconSecondClass} ${this.iconSecondColor ? this.iconSecondColor : this.iconColor}"></i>`);
                    }
                    if (this.iconThirdClass) {
                        if (this.params.value === 'Ocr Usage Report') {
                            htmlParts.push(`<i class="fa gtn-third-icon ${this.iconThirdClass} ${this.iconThirdColor ? this.iconThirdColor : this.iconColor}" style="left: 7px; top: 4px;"></i>`);
                        } else {
                            htmlParts.push(`<i class="fa gtn-third-icon ${this.iconThirdClass} ${this.iconThirdColor ? this.iconThirdColor : this.iconColor}"></i>`);
                        }
                    }
                }
                if (this.params?.data?.isRoleHeader) {
                    className += ' role-header-cell';
                }
                const isActiveUserSessions = this.colDef.headerName === 'Status Tree Node' && this.params.value === 'Active User Sessions';
                htmlParts.push(`<span style="margin-left: ${text ? (isActiveUserSessions ? '5px' : '10px') : '0px'}">${text}</span>`);
                break;
            }
            case CellTemplateType.linkIconLabel: {
                className += 'link-label';
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor} icon-style" style="color: ${this.iconColor}; margin-right: ${text ? '10px' : '0px'}"></i>`);
                }
                htmlParts.push(`<span class="name-link-label" data-cell-action-type="${CellActionType.linkLabelClick}">${text}</span>`);
                if (reviewIconColor) {
                    htmlParts.push(`<span title="${reviewIconTooltip}" class="review-icon-group ${typeof this.params?.data?.isDisabled === 'function' ? this.params?.data?.isDisabled() ? '' : reviewIconColor : ''}"><i class="fa fa-clone"></i><span>R</span></span>`);
                }
                if (claimsIconColor) {
                    htmlParts.push(`<span title="${claimsIconTooltip}" class="review-icon-group ${typeof this.params?.data?.isDisabled === 'function' ? this.params?.data?.isDisabled() ? '' : claimsIconColor : ''}"><i class="fa fa-share-alt-square"></i>`);
                }
                break;
            }
            case CellTemplateType.disabledLinkLabel: {
                className += 'link-label';
                if (!this.params.data.accessAllowed) {
                    className += ' disabled';
                    this.cellElement.className += ' cursor-text';
                }
                htmlParts.push(`<span class="name-link-label" data-cell-action-type="${CellActionType.linkLabelClick}">${text}</span>`);
                if (reviewIconColor) {
                    htmlParts.push(`<span title="${reviewIconTooltip}" class="review-icon-group icon-group-events ${reviewIconColor}"><i class="fa fa-clone"></i><span>R</span></span>`);
                }
                if (claimsIconColor) {
                    htmlParts.push(`<span title="${claimsIconTooltip}" class="review-icon-group icon-group-events ${claimsIconColor}"><i class="fa fa-share-alt-square">`);
                }
                break;
            }
            case CellTemplateType.redLabel: {
                if (this.params.data.count < 0 || this.params.data?.name?.indexOf('(resolvable)') >= 0) {
                    className += ' color-red-05';
                } else if (this.params.data?.name?.indexOf('(unresolvable)') >= 0) {
                    className += ' color-grey-07';
                }
                htmlParts.push(text);
                break;
            }
            case CellTemplateType.boldLabel: {
                if (this.params.data.isBold) {
                    className += 'bold-label';
                }
                htmlParts.push(text);
                break;
            }
            case CellTemplateType.longReadonlyInputLabel:
            case CellTemplateType.readonlyInputLabel: {
                const isLongInput = this.cellTemplateType === CellTemplateType.longReadonlyInputLabel;
                className += isLongInput ? 'long-readonly-input' : 'readonly-input';
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor}" style="color: ${this.iconColor}; margin-right: ${text ? '10px' : '0px'}"></i>`);
                }
                htmlParts.push(`<input type="text" readonly value="${text}" title="${isLongInput ? this.params.data.displayValueTooltip ? this.params.data.displayValueTooltip : text?.trim() : ''}">`);
                break;
            }
            case CellTemplateType.groupIconLabel: {
                className += 'group-label';
                if (this.params.data.level) {
                    htmlParts[0] += ` style="margin-left: ${(this.params.data.level * 15) + 'px'}"`;
                }
                htmlParts.push(`<div>`);
                if (this.params.data.isParent) {
                    htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}" data-cell-action-type="${CellActionType.expandCollapse}"></i>`);
                }
                htmlParts.push(`</div>`);
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor}" style="color: ${this.iconColor}; margin-right: ${text ? '10px' : '0px'}"></i>`);
                }
                htmlParts.push(`<span>${text}</span>`);
                break;
            }
            case CellTemplateType.contextMenu: {
                if (this.params.data instanceof Workflow && this.params.data?.isChild) {
                    className += 'cell-button-hidden';
                    break;
                } else {
                    className += 'cell-button';
                    htmlParts[0] += ` data-cell-action-type="${CellActionType.contextMenu}"`;
                    htmlParts.push(`<img src="assets/images/ellipsis_black.png"></img>`);
                    break;
                }
            }
            case CellTemplateType.jobPriorityTypeButtons: {
                className += 'job-priority-type';

                htmlParts.push(`<button type="button"` +
                    ` data-cell-action-type="${CellActionType.lowPriorityJob}"` +
                    ` data-automation-id="${this.automationId + '-low-priority-job-button'}"` +
                    ` class="${this.params.data.priority === JobPriorityMode.Low ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('Low');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.lowPriorityJob}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.data.priority === JobPriorityMode.Low}"></i>`);
                htmlParts.push('</button>');

                htmlParts.push(`<button type="button"` +
                    ` data-cell-action-type="${CellActionType.mediumPriorityJob}"` +
                    ` data-automation-id="${this.automationId + '-medium-priority-job-button'}"` +
                    ` class="${this.params.data.priority === JobPriorityMode.NORMAL ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('Medium');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.mediumPriorityJob}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.data.priority === JobPriorityMode.NORMAL}"></i>`);
                htmlParts.push('</button>');

                htmlParts.push(`<button type="button"` +
                    ` data-cell-action-type="${CellActionType.highPriorityJob}"` +
                    ` data-automation-id="${this.automationId + '-high-priority-job-button'}"` +
                    ` class="${this.params.data.priority === JobPriorityMode.High ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('High');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.highPriorityJob}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.data.priority === JobPriorityMode.High}"></i>`);
                htmlParts.push('</button>');

                break;
            }
            case CellTemplateType.realmNodesType: {
                className += 'realm-nodes-type';

                htmlParts.push(`<button type="button"` +
                    ` ${this.params.data.hasRealmNodesAddEditPermissions ? '' : 'disabled'}` +
                    ` data-cell-action-type="${CellActionType.selectStandard}"` +
                    ` data-automation-id="${this.automationId + '-standard-button'}"` +
                    ` class="${this.params.value === AnalyticNodeMode.STANDARD ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('Standard');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.selectStandard}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.value === AnalyticNodeMode.STANDARD}"></i>`);
                htmlParts.push('</button>');

                htmlParts.push(`<button type="button"` +
                    ` ${this.params.data.hasRealmNodesAddEditPermissions ? '' : 'disabled'}` +
                    ` data-cell-action-type="${CellActionType.selectExpress}"` +
                    ` data-automation-id="${this.automationId + '-express-button'}"` +
                    ` class="${this.params.value === AnalyticNodeMode.EXPRESS ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('Express');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.selectExpress}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.value === AnalyticNodeMode.EXPRESS}"></i>`);
                htmlParts.push('</button>');

                htmlParts.push(`<button type="button"` +
                    ` ${this.params.data.hasRealmNodesAddEditPermissions ? '' : 'disabled'}` +
                    ` data-cell-action-type="${CellActionType.selectOcr}"` +
                    ` data-automation-id="${this.automationId + '-ocr-button'}"` +
                    ` class="${this.params.value === AnalyticNodeMode.OCR ? 'btn-group-active' : 'btn-group-default'}">`);
                htmlParts.push('OCR');
                htmlParts.push(`<i data-cell-action-type="${CellActionType.selectOcr}"` +
                    ` class="fa fa-caret-up up-caret-active"` +
                    ` [hidden]="${this.params.value === AnalyticNodeMode.OCR}"></i>`);
                htmlParts.push('</button>');
                break;
            }
            case CellTemplateType.cellButton: {
                className += 'cell-button';
                if (this.params.data.isButtonVisible) {
                    if (this.params.data.isButtonEnabled === false) {
                        className += ' disabled';
                    }
                    if (this.params.data.buttonIconClass) {
                        htmlParts[0] += `data-cell-action-type="${CellActionType.cellButton}"`;
                        htmlParts.push(`<i class="fa ${this.params.data.buttonIconClass} ${this.params.data.buttonIconColor}"> </i>`);
                        if (this.params.data.buttonTooltip) {
                            htmlParts[0] += ` title="${this.params.data.buttonTooltip ? this.params.data.buttonTooltip : ''}"`;
                        }
                    }
                } else {
                    className += ' disabled';
                }
                break;
            }
            case CellTemplateType.organizationName:
            case CellTemplateType.organizationDesc: {
                className += 'organization-name';
                className += this.cellTemplateType === CellTemplateType.organizationDesc ? ' organization-description' : '';
                if (this.params.data.isParent) {
                    if (this.cellTemplateType === CellTemplateType.organizationName) {
                        htmlParts.push(`<div data-automation-id="${this.automationId + '-expander-org'}">`);
                        htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}"></i>`);
                    }
                    htmlParts.push(`<span>${text}</span>`);
                }
                break;
            }
            case CellTemplateType.workflowName: {
                className += 'workflow-item-name';
                if (this.params?.data?.isParent) {
                    htmlParts.push(`<div data-automation-id="${this.automationId + '-expander'}">`);
                    htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}"></i>`);
                    htmlParts.push(`<span>${text}</span>`);
                } else {
                    if (this.params?.data?.isChild) {
                        const d = this.params.data as any;
                        const hasSteps = Array.isArray(d.steps) && d.steps.length > 0;
                        const showSynonyms = (d.searchType === 'ADVANCED');
                        const kindDefs = [
                            { label: 'Families', on: !!d.includeFamilyMembers },
                            { label: 'Metadata', on: !!d.expandFields },
                            ...(showSynonyms ? [{ label: 'Synonyms', on: !!d.includeSynonyms }] : [])
                        ];
                        const isLastItem = d.isLastItem;
                        const queryText = d.queryConverted || d.query || '';
                        const safeTitle = queryText.replace(/"/g, '&quot;');
                        const queryHtml = d.queryHighlighted || queryText;

                        const steps = hasSteps ? d.steps : [{
                            title: "Step " + d.orderOfExecution + ": " + d.name,
                            kinds: kindDefs,
                            query: queryText || "",
                            queryHtml: queryHtml || "",
                            tags: Array.isArray(d.parent?.tagItem) ? d.parent?.tagItem : d.parent?.tagItem ? [d.parent?.tagItem] : [],
                            folders: Array.isArray(d.parent?.folderItem) ? d.parent?.folderItem : d.parent?.folderItem ? [d.parent?.folderItem] : []
                        }];

                        if (d.orderOfExecution === 1) {
                            htmlParts.push(`<div class="wf wf--child wf-search-target" data-automation-id="${this.automationId + '-search-target'}"> ${'Initial Search Target: '}<div class="wf wf--child wf-search-target-name">${d.parent.dataSetItem.name}</div></div>`);
                        } else {
                            htmlParts.push(`<div class="wf wf--child" style="display:none;margin-bottom: -43px;"></div>`);
                        }

                        for (let i = 0; i < steps.length; i++) {
                            const s = steps[i] || {};
                            const kinds = (kindDefs).map(k =>
                                `<span class="wf-chip wf-chip--kind ${k.on ? 'wf-chip--on' : 'wf-chip--off'}" data-automation-id="${this.automationId + '-family-options-' + k.label.toLowerCase()}">${k.label}</span>`
                            ).join("");

                            let tags = "";
                            const tagsSource = (Array.isArray(s.tags?.[0]) ? s.tags[0] : s.tags) || [];
                            if (Array.isArray(tagsSource) && tagsSource.length && typeof tagsSource[0]?.name === "string") {
                                tags = tagsSource.map((t: any) =>
                                    `<div class="wf-tag-element"><i class="fa fa-tag folder-icon" style="color: ${t.iconColor}" data-automation-id="${this.automationId + '-tag-icon'}"></i>
                                    <div class="wf-icon-name" data-automation-id="${this.automationId + '-tag-name-' + t.name}"> ${t.name} </div></div>`
                                ).join("");
                            }

                            const folders = (s.folders || []).map((f: any) =>
                                `<div class="wf-folder-element"><i class="fa fa-folder folder-icon" style="color: ${f.attributes[0].value}" data-automation-id="${this.automationId + '-folder-icon'}"></i>
                            <div class="wf-icon-name" data-automation-id="${this.automationId + '-folder-name-' + f.name}"> ${f.name} </div></div>`
                            ).join("");

                            const hasTagsOutput = (tags && isLastItem);
                            const hasFoldersOutput = (folders && isLastItem);
                            const hasAttributes = hasTagsOutput || hasFoldersOutput || isLastItem;

                            htmlParts.push(`
                            <div class="wf-step ${!isLastItem ? 'wf-step-not-last' : 'wf-step-last'}" data-cell-action-type="${CellActionType.workflowStep}">
                                <div class="wf-step-head">
                                <div class="wf-step-title" data-automation-id="${this.automationId + '-step-title-' + i}">${s.title || ('Step ' + (i + 1))}</div>
                                <div class="wf-step-kinds">${kinds}</div>
                                </div>

                                <div class="wf-query" title="${safeTitle}">
                                    <div class="wf-query-inner" data-automation-id="${this.automationId + '-step-query-' + i}">
                                        ${queryHtml}
                                    </div>
                                 </div>
                                ${hasAttributes ? `<div class="wf-attributes">` : ``}
                                    ${(tags && isLastItem) ? `
                                        <div class="wf-aux wf-aux--tags" data-automation-id="${this.automationId + '-tags'}">
                                            ${tags ? `${tags}` : ``}
                                        </div>`
                                    : (isLastItem && tagsSource.length === 0 ? `
                                        <div class="wf-aux wf-aux--end-line italic-font" data-automation-id="${this.automationId + '-no-tags'}">No Tags</div>` : ``)
                                }

                                    ${(folders && isLastItem) ? `
                                        <div class="wf-aux wf-aux--folders" data-automation-id="${this.automationId + '-folders'}">
                                            ${folders ? `${folders}` : `No Folder`}
                                        </div>`
                                    : (isLastItem ? `
                                        <div class="wf-aux wf-aux--folders italic-font" data-automation-id="${this.automationId + '-no-folders'}">No Folder</div>` : ``)
                                }
                                ${hasAttributes ? `</div>` : ``}
                            </div>
                            `);
                        }
                        htmlParts.push(`</div>`);
                    }
                }
                break;
            }
            case CellTemplateType.roleDropdown: {
                className += 'role-dropdown';
                if (this.params.data.roleList) {
                    htmlParts.push(`<select>`);
                    for (let i = 0; i < this.params.data.roleList.length; i++) {
                        let optionsElement = `<option value="${this.params.data.roleList[i].handle}"`;
                        if (this.params.data.selectedRole && this.params.data.roleList[i].handle === this.params.data.selectedRole?.handle) {
                            optionsElement += ` selected`;
                        }
                        optionsElement += `>${this.params.data.roleList[i].name}</option>`;
                        htmlParts.push(optionsElement);
                    }
                    if (isNullOrUndefined(this.params.data.selectedRole)) {
                        let optionsElement = `<option value="" selected disabled hidden></option>`;
                        htmlParts.push(optionsElement);
                    }
                    htmlParts.push(`</select>`);
                }
                break;
            }
            case CellTemplateType.roleDropdownByProjOrOrg: {
                className += 'role-dropdown-by-proj-or-org';
                if (this.params.data.roleList) {
                    htmlParts.push(`<select>`);
                    for (let i = 0; i < this.params.data.roleList.length; i++) {
                        let optionsElement = `<option value="${this.params.data.roleList[i].handle}"`;
                        if (this.params.data.selectedRole && this.params.data.roleList[i].handle === this.params.data.selectedRole?.handle) {
                            optionsElement += ` selected`;
                        }
                        optionsElement += `>${this.params.data.roleList[i].name}</option>`;
                        htmlParts.push(optionsElement);
                    }
                    htmlParts.push(`</select>`);
                }
                break;
            }
            case CellTemplateType.iconSwitch: {
                className += 'icon-switch';
                if (this.params.value) {
                    if ((<any>this.params).iconOnClass) {
                        htmlParts.push(`<i class="fa ${(<any>this.params).iconOnClass}"></i>`);
                    }
                } else {
                    if ((<any>this.params).iconOffClass) {
                        htmlParts.push(`<i class="fa ${(<any>this.params).iconOffClass}"></i>`);
                    }
                }
                break;
            }
            case CellTemplateType.clusteredTopTerms: {
                className += 'drill-cluster';
                if (this.params.data.clusterID) {
                    this.params.data.level = this.params.data.clusterID.split('-').length;
                    htmlParts[0] += ` style="margin-left: ${((this.params.data.level - 1) * 15) + 'px'}"`;
                }
                htmlParts.push(`<div>`);
                if (this.params.data.childViewCount > 0) {
                    htmlParts.push(`<div title="Show subclusters"` +
                        ` data-cell-action-type="${CellActionType.drillCluster}"` +
                        ` data-automation-id="${this.automationId + '-expander-family'}">`);
                    htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-caret-down' : 'fa-caret-right color-grey-08'}"></i>`);
                    htmlParts.push(`</div>`);
                }
                htmlParts.push(`</div>`, `<div>`);
                htmlParts.push(`<span> ${this.params.data.topTerms} </span>`)
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.connectorOrDataAreaName: {
                className += 'connector-or-data-area-name';
                this.params.data.level = this.params.data.isParent ? 1 : 3;
                htmlParts[0] += ` style="margin-left: ${((this.params.data.level - 1) * 15) + 'px'}"`;
                if (this.params.data.level === 1) {
                    htmlParts.push(`<div title="${this.params.data.name}"` +
                        ` data-cell-action-type="${CellActionType.expandCollapse}"` +
                        ` data-automation-id="${this.automationId + '-expander-family'}">`);
                    htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-chevron-down' : 'fa-chevron-right color-grey-10'}"></i>`);
                }
                htmlParts.push(`<i title="${this.params.data.isParent ? 'Connector' : 'Data Area'}" class="fa ${this.params.data.isParent ? 'fa-plug' : 'fa-database color-grey-08'}"></i>`);
                htmlParts.push(`<span title="${this.params.data.name}"> ${this.params.data.name} </span>`)
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.docListFamilyThread: {
                className += 'doc-list-family-thread';
                htmlParts.push(`<div>`);
                if (this.params.data.isParent) {
                    htmlParts.push(`<div title="Expand/collapse family"` +
                        ` data-cell-action-type="${CellActionType.expandCollapse}"` +
                        ` data-automation-id="${this.automationId + '-expander-family'}">`);
                    htmlParts.push(`<i class="fa ${this.params.data.isExpanded ? 'fa-caret-down' : 'fa-caret-right color-grey-10'}"></i>`);
                    htmlParts.push(`</div>`);
                }
                htmlParts.push(`</div>`, `<div>`);
                if (this.params.data.hasFamily) {
                    htmlParts.push(`<div title="View family"` +
                        ` data-cell-action-type="${CellActionType.openFamily}"` +
                        ` data-automation-id="${this.automationId + '-view-family'}">`);
                    htmlParts.push(`<i class="fa fa-paperclip color-green-04"></i>`);
                    htmlParts.push(`</div>`);
                }
                htmlParts.push(`</div>`, `<div>`);
                if (this.params.data.hasThread) {
                    htmlParts.push(`<div title="View thread"` +
                        ` data-cell-action-type="${CellActionType.openThread}"` +
                        ` data-automation-id="${this.automationId + '-view-thread'}">`);
                    htmlParts.push(`<i class="fa fa-link color-blue-01"></i>`);
                    htmlParts.push(`</div>`);
                }
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.tagList: {
                className += 'tag-list';
                if (this.params.data.tagList) {
                    htmlParts.push(`<div>`);
                    if (this.params.data.tagsTooltip) {
                        htmlParts.push(`<i class="fa fa-tags color-grey-08" title="${this.params.data.tagsTooltip}"></i>`);
                    } else {
                        for (let i = 0; i < this.params.data.tagList.length; i++) {
                            htmlParts.push(`<i class="fa fa-tag" style="color: ${this.params.data.tagList[i].displayValue}" title="${this.params.data.tagList[i].name}"></i>`);
                        }
                    }
                    htmlParts.push(`</div>`);
                }
                break;
            }
            case CellTemplateType.tagQueue: {
                className += 'tag-queue';
                if (this.params.data.tagList) {
                    for (let i = 0; i < this.params.data.tagList.length; i++) {
                        htmlParts.push(`<div title="${(this.params.data.tagList[i].tagState === 'ON' ? 'Add Tag: ' : 'Remove Tag: ') + this.params.data.tagList[i].tagName}">`);
                        htmlParts.push(`<i class="fa fa-tag" style="color: ${this.params.data.tagList[i].displayValue}"></i>`);
                        if (this.params.data.tagList[i].tagState === 'NONE') {
                            htmlParts.push(`<span style="color: ${this.params.data.tagList[i].displayValue}">I</span>`);
                        }
                        htmlParts.push(`</div>`);
                    }
                }
                break;
            }
            case CellTemplateType.iconCluster: {
                className += 'icon-drill-cluster';
                htmlParts[0] += ` title="Drill Cluster"`;
                if (this.params.data.childViewCount > 0) {
                    htmlParts[0] += ` data-cell-action-type="${CellActionType.drillCluster}"`;
                    htmlParts.push(`<i class="fa ${this.params.data.clusterID ? 'fa-plus-square-o color-blue-01' : ''}"></i>`);
                }
                break;
            }
            case CellTemplateType.checkbox:
            case CellTemplateType.disabledCheckbox: {
                className += 'checkbox';
                if (this.params.data.isBusy) {
                    htmlParts.push(`<i class="fa fa-spinner fa-spin color-blue-01"></i>`);
                } else if (isBoolean(this.params.value)) {
                    let iElement = `<i class="fa ${this.params.value ? 'fa-check' : ''}`;
                    if (this.cellTemplateType === CellTemplateType.disabledCheckbox || this.params.data.isDisabled || (this.params.data.secureObjectType === SecureObjectGroupType.PROJECT && this.params.column.getColId() === 'VIEW')) {
                        iElement += ` disabled"`;
                    } else {
                        iElement += `" data-cell-action-type="${CellActionType.checkbox}"`;
                    }
                    htmlParts.push(iElement, `></i>`);
                }
                break;
            }
            case CellTemplateType.detailsReport: {
                className += 'details-report';
                htmlParts[0] += ` title="Document Type Details ..."` + ` data-cell-action-type="${CellActionType.linkLabelClick}"`;
                htmlParts.push(`<i class="fa fa-th-list"></i>`);
                break;
            }
            case CellTemplateType.download: {
                if (this.params?.data instanceof ExceptionDetails && this.params.data.reason === 'FILE_NOT_FOUND') {
                    htmlParts[0] += '"';
                } else {
                    className += 'details-report';
                    htmlParts[0] += ` title="Download Document"` + ` data-cell-action-type="${CellActionType.linkLabelClick}"`;
                    htmlParts.push(`<i class="fa fa-download"></i>`);
                }
                break;
            }
            case CellTemplateType.lock: {
                className += 'cell-lock';
                if (this.params.data.lockedBy) {
                    htmlParts.push(`<i class="fa fa-lock" title="${this.params.data.lockedBy}"></i>`);
                }
                break;
            }
            case CellTemplateType.tpAuthUserStatusIconOrLabel: {
                className += 'icon-switch';
                htmlParts.push(`<div title="${this.params?.data?.tpAuthUIUserStatus ? this.params?.data?.tpAuthUIUserStatus : ''}">`);
                if (this.params?.data?.tpAuthStatusIconClass) {
                    htmlParts.push(`<i class="fa ${this.params.data.tpAuthStatusIconClass} ${this.params.data.tpAuthStatusIconColor}"}"></i>`);
                } else {
                    htmlParts.push(`<span style="color: #aaaaaa;">${text}</span>`);
                }
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.userStatus: {
                className += 'icon-switch';
                htmlParts.push(`<div>`);
                if (this.params.data.userStatus === 'locked') {
                    htmlParts.push(`<div title="This user is locked due to too many login attempts."` +
                        ` data-automation-id="${this.automationId + '-locked-user'}">`);
                    htmlParts.push(`<i class="fa fa-lock color-red-05"></i>`);
                    htmlParts.push(`</div>`);
                }
                if (!this.params.data.enabled) {
                    htmlParts.push(`<div title="Disabled User"` +
                        ` data-automation-id="${this.automationId + '-disabled-user'}">`);
                    htmlParts.push(`<i class="fa fa-pause-circle"></i>`);
                    htmlParts.push(`</div>`);
                }
                else {
                    htmlParts.push(`<div data-automation-id="${this.automationId + '-unlocked-user'}">`);
                    htmlParts.push(`</div>`);
                }
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.userAuthenticationMethod: {
                let textValue = text;
                if (this.params.data) {
                    if (this.params.data.authenticationMethod === 'PASSWORD') {
                        textValue = 'Password only';
                    }
                    if (this.params.data.authenticationMethod === 'EMAIL_CODE') {
                        textValue = 'Email Code';
                        htmlParts[0] += `title="Email user a Code on every login attempt"`;
                    }
                    if (this.params.data.authenticationMethod === 'EMAIL_CODE_AND_IP') {
                        textValue = 'Email Code + IP';
                        htmlParts[0] += `title="Email user a Code on every login attempt as long as user's IP is in the whitelisted range"`;
                    }
                    if (this.params.data.authenticationMethod === 'EMAIL_CODE_AND_NOT_IP') {
                        textValue = 'Email Code if not IP';
                        htmlParts[0] += `title="Email user a Code on every login attempt only if user's IP is NOT in the whitelisted range"`;
                    }
                    if (this.params.data.authenticationMethod === 'IP_ONLY') {
                        textValue = 'IP';
                        htmlParts[0] += `title="Allow login only if user's IP is in the whitelisted range"`;
                    }
                    if (this.params.data.authenticationMethod === 'TP_AUTH_ONLY') {
                        textValue = 'TP Auth';
                        htmlParts[0] += `title="TransPerfect Authentication"`;
                    }
                    if (this.params.data.authenticationMethod === 'TP_AUTH_WITH_IP') {
                        textValue = 'TP Auth + IP';
                        htmlParts[0] += `title="TP Auth with Authorized IP addresses"`;
                    }
                }
                htmlParts.push(textValue);
                break;
            }
            case CellTemplateType.nameIconLabelTooltip: {
                htmlParts[0] += `title="${this.params.data.name ? this.params.data.name : ''}"`;
                if (this.params.data.level) {
                    htmlParts[0] += ` style="margin-left: ${(10 + this.params.data.level * 15) + 'px'}"`;
                }
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor}" style="color: ${this.iconColor}; margin-right: ${text ? '10px' : '0px'}" 
                    title="${this.params.data.tooltip ? this.params.data.tooltip : ''}" ></i>`);
                }
                htmlParts.push(text);
                break;
            }
            case CellTemplateType.editableNumericInput: {
                htmlParts.push(`<input style="width: 50px;" type="number" min="1" max="100" data-cell-action-type="${CellActionType.jobManagementInput}" value="${this.params.data.desiredRate}">`);
                htmlParts.push(`<span title="${'Total cores: ' + this.params.data.totalCores + "\nAvailable cores: " + this.params.data.availableThreads + "\nAssigned cores: " + this.params.data.numberThreads}">${' / ' + this.params.data.currentRate + ' %'}</span>`);
                break;
            }
            case CellTemplateType.editableDeactivatedThesholdInput:
            case CellTemplateType.editableReactivatedThesholdInput: {
                if (this.params.data.storageUseType !== StorageUseType.INDEX_STORE) {
                    htmlParts.push(`<input ` + `${this.params.data.inUse && this.params.data.hasEditThresholdPermission ? '' : 'disabled '}` + `type="text" maxlength="3" data-cell-action-type="${CellActionType.editableInput}" class="editable-threshold-input" onkeypress="return (event.charCode == 8 || event.charCode == 0) ? null : event.charCode >= 48 && event.charCode <= 57" data-cell-action-type="${CellActionType.jobManagementInput}" value="${this.cellTemplateType === CellTemplateType.editableDeactivatedThesholdInput ? this.params.data.deactivationThreshold : this.params.data.reactivationThreshold}">`);
                    htmlParts.push(`<span>%</span>`);
                }
                break;
            }
            case CellTemplateType.jobManagementRate: {
                htmlParts.push(`<span title="${'Total cores: ' + this.params.data.totalCores + "\nAvailable cores: " + this.params.data.availableThreads + "\nAssigned cores: " + this.params.data.numberThreads}">${this.params.data.desiredRate + '% / ' + this.params.data.currentRate + ' %'}</span>`);
                break;
            }
            case CellTemplateType.jobManagementCores: {
                htmlParts.push(`<div style="line-height: 15px;" title="${'Total: ' + this.params.data.totalCores + '\n' + "Available: " + this.params.data.availableThreads + '\n' + "Assigned: " + this.params.data.numberThreads}">`);
                htmlParts.push(`<div>${'Total: ' + this.params.data.totalCores}</div>`);
                htmlParts.push(`<div>${'Available: ' + this.params.data.availableThreads}</div>`);
                htmlParts.push(`<div>${'Assigned: ' + this.params.data.numberThreads}</div>`);
                htmlParts.push(`</div>`);
                break;
            }
            case CellTemplateType.jobManagementComplete: {
                htmlParts.push(text + ' %');
                break;
            }
            case CellTemplateType.computedDuration: {
                const html = _.cloneDeep(htmlParts[0]);
                const timeParts = text.split(':');
                let days = Math.floor(timeParts[0] / 24);
                const initialHours = this.getHours(timeParts);
                const duration = this.formatTimeWithoutExtraZeros(days, initialHours, timeParts[1], timeParts[2]);
                const titleDiv = `<div style="margin: 0;text-align: right;" title="${this.getTitleForField(this.params.data, this.colDef.field)}">`;
                htmlParts.push(titleDiv);
                htmlParts.push(`<span>${duration}</span>`);
                htmlParts.push(`</div>`);
                if (!this.params.data.durationNoCounter) {
                    setInterval(() => {
                        timeParts[2] = parseInt(timeParts[2]) + 1;
                        if (timeParts[2] === 60) {
                            timeParts[2] = '00';
                            timeParts[1] = parseInt(timeParts[1]) + 1;
                            if (timeParts[1] === 60) {
                                timeParts[1] = '00';
                                timeParts[0] = parseInt(timeParts[0]) + 1;
                            }
                        }

                        if (timeParts[0] === '00' && timeParts[1] === '00' && timeParts[2] === '00') {
                            days = days + 1;
                        }

                        for (let i = htmlParts.length - 1; i >= 1; i--) {
                            htmlParts.splice(i, 1);
                        }
                        htmlParts.push(html);

                        const hours = this.getHours(timeParts);
                        const computedDuration = this.formatTimeWithoutExtraZeros(days, hours, timeParts[1], timeParts[2]);
                        htmlParts.push(titleDiv);
                        htmlParts.push(`<span>${computedDuration}</span>`);
                        htmlParts.push(`</div>`);
                        this.cellElement.innerHTML = htmlParts.join('');
                    }, 1000);
                }
                break;
            }
            case CellTemplateType.iconOnlyFieldWithTooltip: {
                if (this.iconClass) {
                    htmlParts.push(`<i class="fa ${this.iconClass} ${this.iconColor}" style="color: ${this.iconColor}" 
                    title="${this.params.data.tooltip ? this.params.data.tooltip : this.iconToolTip ? this.iconToolTip : ''}" ></i>`);
                }
                break;
            }
            case CellTemplateType.textWithStatus: {
                className += 'text-with-status';
                if (this.params.data) {
                    htmlParts.push(`
                    <span data-automation-id="${this.automationId + '-value'}">${this.params.data.value}</span>
                    <span data-automation-id="${this.automationId + '-status'}" class="${this.params.data.className}">${this.params.data.status}</span>
                  `);
                }
                break;
            }
        }
        if (className) {
            htmlParts[0] += ` class="${className}"`;
        }
        htmlParts[0] += `>`;
        htmlParts.push('</span>');
        this.cellElement.innerHTML = htmlParts.join('');

        if (this.cellTemplateType === CellTemplateType.editableDeactivatedThesholdInput || this.cellTemplateType === CellTemplateType.editableReactivatedThesholdInput) {
            const inputElement = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.editableInput}"]`) as HTMLElement;
            if (inputElement && this.params.node?.data) {
                inputElement.addEventListener('keydown', (ev: KeyboardEvent) => {
                    if (ev.key === 'Tab') {
                        return;
                    }
                    ev.stopPropagation();
                });
                inputElement.onkeyup = (ev: Event) => {
                    if (ev?.currentTarget && ev.currentTarget instanceof HTMLInputElement) {
                        const inputElement = ev.currentTarget as HTMLInputElement;
                        const threshold = (inputElement.value === '') ? 0 : parseInt(inputElement.value);
                        if (threshold < 1 || threshold > 100) {
                            this.params.node.data.thresholdHasChanged = false;
                        }
                        else {
                            this.setThresholdData(inputElement, threshold);
                        }
                    }
                }
                inputElement.onchange = (ev: Event) => {
                    if (ev?.currentTarget && ev.currentTarget instanceof HTMLInputElement) {
                        const inputElement = ev.currentTarget as HTMLInputElement;
                        const threshold = (inputElement.value === '') ? 0 : parseInt(inputElement.value);
                        if (threshold < 1 || threshold > 100) {
                            this.setThresholdData(inputElement, threshold);
                        }
                    }
                }
            }
        }
        if (this.params.data instanceof Job) {
            this.contextMenuElement = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.contextMenu}"]`);
            if (this.contextMenuElement && this.params.api && this.params.node?.data) {
                //enable/disable context menu cell based on number of selected rows from Job Management grid
                this.params.api.addEventListener('selectionChanged', () => {
                    if (this.params.api.getSelectedRows()?.length > 1 && this.params.node?.isSelected()) {
                        this.params.node.data.hasDisabledContextMenu = true;
                        this.contextMenuElement.classList.add('disabled-context-menu');
                    } else {
                        this.contextMenuElement.classList.remove('disabled-context-menu');
                        this.params.node.data.hasDisabledContextMenu = false;
                    }
                });
            }
            if (this.cellTemplateType === CellTemplateType.jobPriorityTypeButtons) {
                // prevent row selection on updating the priority value on a row
                const mediumPriorityJobInput = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.mediumPriorityJob}"]`) as HTMLElement,
                    lowPriorityJobInput = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.lowPriorityJob}"]`) as HTMLElement,
                    highPriorityJobInput = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.highPriorityJob}"]`) as HTMLElement;
                if ((mediumPriorityJobInput || lowPriorityJobInput || highPriorityJobInput) && this.params.node?.data) {
                    mediumPriorityJobInput.onclick = (ev: MouseEvent) => {
                        if (!ev?.shiftKey) {
                            this.params.node.selectable = false;
                        }
                    }

                    lowPriorityJobInput.onclick = (ev: MouseEvent) => {
                        if (!ev?.shiftKey) {
                            this.params.node.selectable = false;
                        }
                    }

                    highPriorityJobInput.onclick = (ev: MouseEvent) => {
                        if (!ev?.shiftKey) {
                            this.params.node.selectable = false;
                        }
                    }
                }
            }
            if (this.cellTemplateType === CellTemplateType.editableNumericInput) {
                const jobInputElement = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.jobManagementInput}"]`) as HTMLElement;
                if (jobInputElement && this.params.node?.data) {
                    jobInputElement.onchange = (ev: Event) => {
                        if (ev?.currentTarget && ev.currentTarget instanceof HTMLInputElement) {
                            const inputElement = ev.currentTarget as HTMLInputElement;
                            const desiredRate = (inputElement.value === '') ? 1 : parseInt(inputElement.value);
                            this.params.node.data.desiredRate = desiredRate < 1 ? 1 : (desiredRate > 100 ? 100 : desiredRate);
                            this.params.node.data.rateHasChanged = this.params.node.data.desiredRate !== this.params.node.data.originalDesiredRate;
                            //don't know the reason but need both of the assignments below even though I think they do the same thing 
                            inputElement.setAttribute('value', this.params.node.data.desiredRate.toString());
                            inputElement.value = this.params.node.data.desiredRate.toString();
                        }
                    }
                    jobInputElement.onclick = (ev: MouseEvent) => {
                        if (!ev?.shiftKey) {
                            ev.preventDefault();
                            ev.stopPropagation();
                        }
                    }
                    jobInputElement.onkeyup = (event: KeyboardEvent): boolean => {
                        if (event?.currentTarget && event.currentTarget instanceof HTMLInputElement) {
                            const inputElement = event.currentTarget as HTMLInputElement;
                            const charCode = event.key ? parseInt(event.key) : 0;
                            if (this.params?.node?.data) {
                                const desiredRate = (inputElement.value === '') ? 1 : parseInt(inputElement.value);
                                this.params.node.data.desiredRate = desiredRate;
                                this.params.node.data.rateHasChanged = this.params.node.data.desiredRate !== this.params.node.data.originalDesiredRate;
                            }
                            if (inputElement.value?.length === 0 && charCode == 48) {
                                return false;
                            }
                            if (event) {
                                return (charCode === 8 || charCode === 0) ? false : charCode >= 48 && charCode <= 57;
                            }
                            return false;
                        }
                    }
                }
            }
        }

        if (this.cellTemplateType === CellTemplateType.workflowName) {

            const step: any = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.workflowStep}"]`) as HTMLElement;
            if (step) {
                setTimeout(() => {

                    const head = step.querySelector('.wf-step-head') as HTMLElement;
                    const query = step.querySelector('.wf-query') as HTMLElement;
                    const attributes = step.querySelector('.wf-attributes') as HTMLElement;

                    const headHeight = head?.scrollHeight ?? 0;
                    const queryHeight = query?.scrollHeight > 90 ? 90 : query?.scrollHeight ?? 0;
                    const attrHeight = attributes?.scrollHeight ?? 0;

                    const padding = this.params.data.isLastItem ? (this.params.data.isFirstItem ? 40 : (query?.scrollHeight > 100 ? 20 : 13)) : (this.params.data.isFirstItem ? 30 : (query?.scrollHeight === 100 ? 10 : 0));

                    const total =
                        headHeight +
                        queryHeight +
                        attrHeight +
                        padding;

                    this.params.node.setRowHeight(total);
                    this.params.api.onRowHeightChanged();

                    const attrEls = step.querySelectorAll('.wf-attributes');

                    attrEls.forEach((attrEl: Element) => {
                        const tagsEl = attrEl.querySelector('.wf-aux--tags') as HTMLElement;
                        if (!tagsEl) return;

                        const tagEls = tagsEl.querySelectorAll('.wf-tag-element') as NodeListOf<HTMLElement>;
                        if (tagEls.length === 0) {
                            attrEl.classList.remove('wf-attributes--wrapped');
                            attrEl.classList.add('wf-attributes--not-wrapped');
                            return;
                        }

                        const singleTagHeight = tagEls[0].getBoundingClientRect().height;
                        const containerHeight = tagsEl.getBoundingClientRect().height;

                        if (containerHeight > singleTagHeight * 1.5) {
                            this.params.data.hasWrappedTags = true;
                            attrEl.classList.add('wf-attributes--wrapped');
                            attrEl.classList.remove('fixed-row');
                            attrEl.classList.remove('wf-attributes--not-wrapped');
                        } else {
                            attrEl.classList.remove('wf-attributes--wrapped');
                            attrEl.classList.add('fixed-row');
                            attrEl.classList.add('wf-attributes--not-wrapped');
                        }

                    });
                    const queryEl = step.querySelector('.wf-query');
                    const hasVerticalScroll = queryEl.scrollHeight > queryEl.clientHeight;

                    if (hasVerticalScroll) {
                        queryEl.classList.add("wf-query-scrollable");
                    } else {
                        queryEl.classList.remove("wf-query-scrollable");
                    }
                });
            }
        }

        // post build events
        if (this.cellTemplateType === CellTemplateType.roleDropdown || this.cellTemplateType === CellTemplateType.roleDropdownByProjOrOrg) {
            const select = this.cellElement.querySelector('select');
            if (select) {
                select.onchange = (ev: Event) => {
                    const selectedOption = (this.params.data.roleList as Array<any>).filter(r => r.handle === (ev.currentTarget as HTMLSelectElement).value).pop();
                    for (let i = 0; i < select.options.length; i++) {
                        select.options[i].removeAttribute('selected');
                    }
                    const option = select.options.item(select.selectedIndex);
                    option.setAttribute('selected', '');
                    this.params.data.selectedRole = selectedOption;
                };
                if (this.cellTemplateType === CellTemplateType.roleDropdownByProjOrOrg) {
                    select.onclick = (ev: Event) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                    };
                }
            }
        } else {
            const nonSelectableElement = <HTMLElement>this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.contextMenu}"],[data-cell-action-type="${CellActionType.expandCollapse}"]`);
            const checkboxColumn = this.params.api.getColumns().find(c => c.getColId() === 'checkbox');
            if (nonSelectableElement && checkboxColumn) {
                nonSelectableElement.onclick = (ev: MouseEvent) => {
                    if (!ev.shiftKey) {
                        this.params.node.selectable = false;
                    }
                };
            }
        }
    }

    setThresholdData(inputElement: HTMLInputElement, threshold: number) {
        if (this.cellTemplateType === CellTemplateType.editableDeactivatedThesholdInput) {
            this.params.node.data.deactivationThreshold = threshold < 1 ? 1 : (threshold > 100 ? 100 : threshold);
            this.params.node.data.thresholdHasChanged = this.params.node.data.deactivationThreshold !== this.params.node.data.initialDeactivationThreshold;
            //don't know the reason but need both of the assignments below even though I think they do the same thing 
            inputElement.setAttribute('value', this.params.node.data.deactivationThreshold.toString());
            inputElement.value = this.params.node.data.deactivationThreshold.toString();
        } else {
            this.params.node.data.reactivationThreshold = threshold < 1 ? 1 : (threshold > 100 ? 100 : threshold);
            this.params.node.data.thresholdHasChanged = this.params.node.data.reactivationThreshold !== this.params.node.data.initialReactivationThreshold;
            //don't know the reason but need both of the assignments below even though I think they do the same thing 
            inputElement.setAttribute('value', this.params.node.data.reactivationThreshold.toString());
            inputElement.value = this.params.node.data.reactivationThreshold.toString();
        }
    }

    getTitleForField(fieldEntry: any, fieldName): string {
        switch (fieldName) {
            case 'sessionDuration':
                return 'Logged in: ' + fieldEntry.sessionTime;
            case 'idleDuration':
                return 'Last activity: ' + fieldEntry.lastPollTime;
            case 'processingDuration':
                return 'Started: ' + fieldEntry.startTime;
            default: return '';
        }
    }

    getHours(time): number {
        const remainder = time[0] % 24;
        return Math.floor(remainder);
    }

    addZeroToTime(time): any {
        if (time > 0 && time < 10 && time.toString().length == 1) {
            time = '0' + time;
        }
        if (time === 0) {
            time = '00';
        }
        return time;
    }

    formatTimeWithoutExtraZeros(days: number, hours: number, mins: string, secs: string): string {
        const formattedSecs = ':' + this.addZeroToTime(secs);
        const timeWithMinsAndSecs = this.addZeroToTime(mins) + formattedSecs;
        if (days !== 0) {
            return days + 'd ' + this.addZeroToTime(hours) + ':' + timeWithMinsAndSecs;
        } else {
            if (hours === 0) {
                if (mins === '00') {
                    return formattedSecs;
                } else {
                    return parseInt(mins) + formattedSecs;
                }
            } else {
                return hours + ':' + timeWithMinsAndSecs;
            }
        }
    }

    getGui(): HTMLElement {
        return this.cellElement;
    }

    refresh(params: ICellRendererParams): boolean {
        return false;
    }

    onCellContextMenu = (event: MouseEvent): void => {
        if (this.params && this.gridContext && this.gridContext.onCellContextMenu) {
            this.gridContext.onCellContextMenu(this.params, event);
        }
    }
}
